import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toastNotification } from "../../../services/notification-service";

const PublishDiscussionModal = ({ isOpen, onClose, onSubmit, title }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: title || "",
    categories: [],
    user_ids: [],
    tag_ids: [],
    emails: [],
    status: 1,
    pictures: [],
  });

  useEffect(() => {
    setFormData((prev) => ({ ...prev, description: title || "" }));
  }, [title]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const parsedValue = name === "status" ? Number(value) : value;
    setFormData((prev) => ({
      ...prev,
      [name]: parsedValue,
    }));
  };


  const handleQuillChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      title: value,
    }));
  };

  const handleMultiSelectChange = (e, key) => {
    const selected = [...e.target.options]
      .filter((option) => option.selected)
      .map((option) => Number(option.value));
    setFormData((prev) => ({
      ...prev,
      [key]: selected,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (formData.pictures.length + files.length > 3) {
      toastNotification({
        status: "error",
        message: "You can upload a maximum of 3 pictures",
      });
      return;
    }
    
    const imageFiles = files.filter(file => file.type.startsWith("image/"));
    
    if (imageFiles.length !== files.length) {
      toastNotification({
        status: "warning",
        message: "Only image files are allowed",
      });
    }
    
    if (imageFiles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        pictures: [...prev.pictures, ...imageFiles].slice(0, 3)
      }));
    }
  };

  const removePicture = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      pictures: prev.pictures.filter((_, i) => i !== indexToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.description.trim()) {
      toastNotification({
        status: "error",
        message: "Title is required",
      });
      return;
    }

    const formDataToSend = new FormData();

    formDataToSend.append("title", formData.title);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("status", formData.status);

    formData.categories.forEach((cat) =>
      formDataToSend.append("categories[]", cat)
    );
    formData.tag_ids.forEach((tag) => formDataToSend.append("tag_ids[]", tag));

    formData.pictures.forEach((file, index) => {
      formDataToSend.append(`pictures[${index}]`, file);
    });

    try {
      await onSubmit(formDataToSend);

      setFormData({
        title: "",
        description: "",
        categories: [],
        user_ids: [],
        tag_ids: [],
        emails: [],
        status: 1,
        pictures: [],
      });

      onClose();
    } catch (error) {
      toastNotification({
        status: "error",
        message: error.message || "Failed to submit discussion",
      });
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div className="bg-orange-600 px-6 py-4">
                  <Dialog.Title className="text-lg font-semibold text-white">
                    Publish New Discussion
                  </Dialog.Title>
                </div>

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="space-y-6 p-6">
                  {/* Title */}
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Discussion Title
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="description"
                      value={formData?.description}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition"
                      placeholder="Enter a descriptive title"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Detailed Description
                    </label>
                    <div className="rounded-lg border border-gray-300 overflow-hidden">
                      <ReactQuill
                        theme="snow"
                        value={formData?.title}
                        onChange={handleQuillChange}
                        className="h-48 bg-white"
                        placeholder="Write your discussion content here..."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="categories"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Topics
                      </label>
                      <select
                        multiple
                        id="categories"
                        name="categories"
                        value={formData.categories}
                        onChange={(e) =>
                          handleMultiSelectChange(e, "categories")
                        }
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 min-h-[42px] focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition"
                      >
                        {categoriesList.map(({ id, name }) => (
                          <option key={id} value={id} className="p-2">
                            {name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Hold Ctrl/Cmd to select multiple
                      </p>
                    </div>

                    <div>
                      <label
                        htmlFor="tags"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Tags
                      </label>
                      <select
                        multiple
                        id="tags"
                        name="tag_ids"
                        value={formData.tag_ids}
                        onChange={(e) => handleMultiSelectChange(e, "tag_ids")}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 min-h-[42px] focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition"
                      >
                        {tagsList.map(({ id, tag_title }) => (
                          <option key={id} value={id} className="p-2">
                            {tag_title}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Hold Ctrl/Cmd to select multiple
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments (Max 3 images)
                    </label>
                    <div className="flex items-center gap-4">
                      <label className="flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <svg
                            className="w-8 h-8 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                          </svg>
                          <p className="text-xs text-gray-500 mt-2">
                            Click to upload
                          </p>
                        </div>
                        <input
                          id="file-upload"
                          type="file"
                          className="hidden"
                          multiple
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                      </label>

                      <div className="flex gap-2">
                        {formData.pictures.map((file, index) => (
                          <div
                            key={index}
                            className="relative w-32 h-32 rounded-lg overflow-hidden border border-gray-200"
                          >
                            {file.type.startsWith("image/") ? (
                              <>
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`Preview ${index}`}
                                  className="w-full h-full object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() => removePicture(index)}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition"
                                >
                                  ×
                                </button>
                              </>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                <span className="text-xs text-gray-500">
                                  File
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-5 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition"
                      onClick={(e) => {
                        handleSubmit(e);
                      }}
                    >
                      Publish Discussion
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

const categoriesList = [
  {
    id: 1,
    name: "Strategic Sourcing prod",
  },
  {
    id: 2,
    name: "Category Management",
  },
  {
    id: 3,
    name: "Contracting",
  },
  {
    id: 4,
    name: "Purchasing",
  },
  {
    id: 5,
    name: "Payment & Help Desk",
  },
  {
    id: 6,
    name: "Biotech",
  },
  {
    id: 7,
    name: "CDMO",
  },
  {
    id: 8,
    name: "CRO",
  },
  {
    id: 9,
    name: "Healthcare",
  },
];

const tagsList = [
  {
    id: 4,
    tag_title: "Finance",
  },
  {
    id: 5,
    tag_title: "Financing",
  },
  {
    id: 6,
    tag_title: "Procurement conference",
  },
  {
    id: 7,
    tag_title: "Procurement events",
  },
  {
    id: 8,
    tag_title: "Supply chain",
  },
  {
    id: 9,
    tag_title: "Procurement",
  },
  {
    id: 11,
    tag_title: "Interview",
  },
  {
    id: 13,
    tag_title: "IT",
  },
  {
    id: 14,
    tag_title: "IT category expert",
  },
  {
    id: 15,
    tag_title: "Procurement openings",
  },
  {
    id: 17,
    tag_title: "Supply chain management",
  },
  {
    id: 18,
    tag_title: "Purchasing management",
  },
  {
    id: 19,
    tag_title: "Procurement jobs",
  },
  {
    id: 20,
    tag_title: "Sourcing certificate",
  },
  {
    id: 21,
    tag_title: "Supplier performance",
  },
  {
    id: 22,
    tag_title: "SPM",
  },
  {
    id: 23,
    tag_title: "Spend analysis",
  },
  {
    id: 24,
    tag_title: "Spend management",
  },
  {
    id: 25,
    tag_title: "Outsourcing",
  },
  {
    id: 26,
    tag_title: "Shared services",
  },
  {
    id: 27,
    tag_title: "Buying Center",
  },
  {
    id: 28,
    tag_title: "Buy Desk",
  },
  {
    id: 29,
    tag_title: "Robotics",
  },
  {
    id: 30,
    tag_title: "Vendor master",
  },
  {
    id: 31,
    tag_title: "Material master",
  },
  {
    id: 32,
    tag_title: "Procurement certification",
  },
  {
    id: 33,
    tag_title: "Import",
  },
  {
    id: 34,
    tag_title: "Export",
  },
  {
    id: 35,
    tag_title: "Trade deficit",
  },
  {
    id: 36,
    tag_title: "Masterdata Management",
  },
  {
    id: 37,
    tag_title: "Category Management",
  },
  {
    id: 38,
    tag_title: "TCO",
  },
  {
    id: 39,
    tag_title: "Strategic sourcing",
  },
  {
    id: 40,
    tag_title: "CPO",
  },
  {
    id: 41,
    tag_title: "Supply chain finance",
  },
  {
    id: 42,
    tag_title: "Inventory Management",
  },
  {
    id: 43,
    tag_title: "Accounting",
  },
  {
    id: 44,
    tag_title: "Digitalisation",
  },
  {
    id: 45,
    tag_title: "Professionalisation",
  },
  {
    id: 46,
    tag_title: "Innovation",
  },
  {
    id: 47,
    tag_title: "Job",
  },
  {
    id: 48,
    tag_title: "Consulting",
  },
  {
    id: 49,
    tag_title: "Negotiations",
  },
  {
    id: 50,
    tag_title: "Purchasing",
  },
  {
    id: 51,
    tag_title: "DEMAND PLANNING",
  },
  {
    id: 52,
    tag_title: "Government",
  },
  {
    id: 53,
    tag_title: "Public Procurement",
  },
  {
    id: 54,
    tag_title: "Energy Equipment & Services",
  },
  {
    id: 55,
    tag_title: "Oil & Gas",
  },
  {
    id: 56,
    tag_title: "Chemicals",
  },
  {
    id: 57,
    tag_title: "Metals & Mining",
  },
  {
    id: 58,
    tag_title: "Paper & Forest Products",
  },
  {
    id: 59,
    tag_title: "Construction & Engineering",
  },
  {
    id: 60,
    tag_title: "Professional Services",
  },
  {
    id: 61,
    tag_title: "Airlines",
  },
  {
    id: 62,
    tag_title: "Marine",
  },
  {
    id: 63,
    tag_title: "Road & Rail",
  },
  {
    id: 64,
    tag_title: "Automobiles",
  },
  {
    id: 65,
    tag_title: "Household Durables",
  },
  {
    id: 66,
    tag_title: "Media",
  },
  {
    id: 67,
    tag_title: "Retail & Distribution",
  },
  {
    id: 68,
    tag_title: "Food Products",
  },
  {
    id: 69,
    tag_title: "Health Care Providers & Services",
  },
  {
    id: 70,
    tag_title: "Pharmaceuticals & Life Sciences",
  },
  {
    id: 71,
    tag_title: "Banks, Finance & Capital Markets",
  },
  {
    id: 72,
    tag_title: "Insurance",
  },
  {
    id: 73,
    tag_title: "Internet Software & Services",
  },
  {
    id: 74,
    tag_title: "Technology Hardware, Storage & Peripherals",
  },
  {
    id: 75,
    tag_title: "Semiconductors & Semiconductor Equipment",
  },
  {
    id: 76,
    tag_title: "Wireless Telecommunication Services",
  },
  {
    id: 77,
    tag_title: "Electric Utilities",
  },
  {
    id: 78,
    tag_title: "Gas Utilities",
  },
  {
    id: 79,
    tag_title: "Multi-Utilities",
  },
  {
    id: 80,
    tag_title: "Water Utilities",
  },
  {
    id: 81,
    tag_title: "Real Estate Management & Development",
  },
  {
    id: 82,
    tag_title: "HPMF - Hotels & Hospitality",
  },
  {
    id: 83,
    tag_title: "Esourcing",
  },
  {
    id: 84,
    tag_title: "P2P - Process",
  },
  {
    id: 85,
    tag_title: "P2P - Tech",
  },
  {
    id: 86,
    tag_title: "S2P - Process",
  },
  {
    id: 87,
    tag_title: "S2P - Tech",
  },
  {
    id: 88,
    tag_title: "Market Research",
  },
  {
    id: 89,
    tag_title: "Benchmarking",
  },
  {
    id: 90,
    tag_title: "Service Provider",
  },
  {
    id: 91,
    tag_title: "Global Sourcing",
  },
  {
    id: 92,
    tag_title: "Low Cost Scountry Sourcing",
  },
  {
    id: 93,
    tag_title: "Contractor Management",
  },
  {
    id: 94,
    tag_title: "Vendor Management System - VMS",
  },
  {
    id: 95,
    tag_title: "Managed Service Provider MSP",
  },
  {
    id: 96,
    tag_title: "Warehouse Management System WMS",
  },
  {
    id: 97,
    tag_title: "Logistics",
  },
  {
    id: 98,
    tag_title: "Freight",
  },
  {
    id: 99,
    tag_title: "Transportation",
  },
  {
    id: 100,
    tag_title: "Ocean Freight",
  },
  {
    id: 101,
    tag_title: "Procure Class",
  },
  {
    id: 102,
    tag_title: "Procurement Training",
  },
  {
    id: 103,
    tag_title: "Procurement Certificate",
  },
  {
    id: 104,
    tag_title: "Pharmaceutical Procurement",
  },
  {
    id: 105,
    tag_title: "Raw Materials Procurement",
  },
  {
    id: 106,
    tag_title: "Supplier Selection",
  },
  {
    id: 107,
    tag_title: "Pharmaceutical Supply Chain",
  },
  {
    id: 108,
    tag_title: "Procurement Strategy",
  },
  {
    id: 109,
    tag_title: "Quality Assurance",
  },
  {
    id: 110,
    tag_title: "Supplier Relationship Management",
  },
  {
    id: 111,
    tag_title: "Regulatory Compliance",
  },
  {
    id: 112,
    tag_title: "Cost Optimization",
  },
  {
    id: 113,
    tag_title: "Counterfeit Drug Mitigation",
  },
  {
    id: 114,
    tag_title: "Sustainability in Procurement",
  },
  {
    id: 115,
    tag_title: "Digital Procurement Technologies",
  },
  {
    id: 116,
    tag_title: "Compliance in Pharma Procurement",
  },
  {
    id: 117,
    tag_title: "API Procurement",
  },
  {
    id: 118,
    tag_title: "Pharma",
  },
  {
    id: 119,
    tag_title: "Biotech",
  },
  {
    id: 120,
    tag_title: "Cdmo",
  },
  {
    id: 121,
    tag_title: "Direct_procurement",
  },
  {
    id: 122,
    tag_title: "Healthcare",
  },
  {
    id: 123,
    tag_title: "Sourcing",
  },
  {
    id: 124,
    tag_title: "Cro",
  },
  {
    id: 125,
    tag_title: "ESG",
  },
];

export default PublishDiscussionModal;
